import axios, { AxiosHeaders } from "axios";
import { COMMONCONSTANTS } from "../data/constants";
import { GLOBALMESSAGES } from "../constants/messages";
import { getAccessToken } from "./commonHelper";
import { getValidAccessToken } from "./accessTokenHelper";
import { requestNonce } from "../services/nonceService";

const baseAPIUrl = process.env.REACT_APP_API_URL + "/api";

const apiClient = axios.create({
  baseURL: baseAPIUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// INTERCEPTOR 1: Auth token
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  config.headers["Log-Source"] = COMMONCONSTANTS.LOG_SOURCE;
  return config;
});

// INTERCEPTOR 2: 401 retry + network error normalization
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // No HTTP response means a network-level failure (ERR_NETWORK, ECONNABORTED, etc.).
    // Axios surfaces different strings for each failure mode, producing inconsistent
    // error toasts. Normalize them here — at the HTTP boundary — so every caller
    // gets one consistent message regardless of which failure mode fired.
    if (!error.response) {
      return Promise.reject(new Error(GLOBALMESSAGES.NETWORK_ERROR));
    }
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await getValidAccessToken();
        if (newAccessToken) {
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return apiClient(originalRequest);
        }
      } catch {
        // Refresh token unavailable — tokens already cleared, redirect in progress
      }
      // Refresh failed: getValidAccessToken redirected to /signin.      
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

const nonceProtectedEndpoints = [
  "/PortalUser/send-otp",
  "/PortalUser/verify-otp",
  "/PortalUser/verify-sign-in",
  "/PortalUser/password-settings",
  "PortalUser/signup-auth-mode",
  "PortalUser/verify-tfa-code",
  "PortalUser/verify-tfa-code-by-userid",
  "PortalUser/update-password",
  "PortalUser/verify-captcha",
  "PortalUser/user",
  "PortalUser/secret-key",
  "PortalUser/generate-qr-code",
  "portaluser/secret-value",
  "PortalUser/details",
  "UserProfile/reset-password",
  "UserProfile/verify-current-password",
  "Portal/error", 
  "Portal/portal-user" 
];

// INTERCEPTOR 3: Nonce — added LAST so it runs FIRST (Axios reverses order)
// This ensures nonce is fetched as late as possible, minimizing expiry risk
apiClient.interceptors.request.use(async (config) => {
  const url = config.url ?? "";
  const isNonceRequest = url.includes("/nonce/request");
  const isProtected = nonceProtectedEndpoints.some((e) => url.includes(e));

  if (!isNonceRequest && isProtected) {
    try {
      const { nonce, fingerprint } = await requestNonce();
      const headers = config.headers as AxiosHeaders;
      headers.set("X-Nonce", nonce);
      headers.set("X-Fingerprint", fingerprint);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  return config;
});

export default apiClient;